import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { useAuthContext } from "@/contexts/AuthContext";
import { useLingui } from "@lingui/react";
import { toast } from "sonner";
import { t } from "@lingui/macro";
import type { CapacitySettings } from "@/types";
import { endpointKey } from "@/lib/queryKeys";
import { OrganisationSettingsService } from "@/api/services/OrganisationSettingsService";
import type { organisations_CapacitySettingsRequest } from '@/api';
// Deep-imported so a `vi.mock('@/api')` cannot leave the enum object undefined.
import { models_StaffingRequirements } from '@/api/models/models_StaffingRequirements';

/**
 * `StaffingRequirements['model']` is a string-literal union while the generated DTO
 * uses a nominal enum with the SAME two values. The mapping below substitutes the
 * member exhaustively — no cast, no runtime change.
 */
function toCapacitySettingsRequest(
  settings: CapacitySettings,
): organisations_CapacitySettingsRequest {
  const { staffing_requirements: staffing, ...rest } = settings;
  if (!staffing) return rest;
  return {
    ...rest,
    staffing_requirements: {
      ...staffing,
      model:
        staffing.model === 'ratio'
          ? models_StaffingRequirements.model.RATIO
          : models_StaffingRequirements.model.THRESHOLD,
    },
  };
}


export const DEFAULT_WARNING_THRESHOLD = 100;
export const DEFAULT_CRITICAL_THRESHOLD = 120;

// The endpoint's own key (#2193 B4): the setup checklist reads the same entry.
export const capacitySettingsKeys = {
  settings: (orgId: string) => endpointKey(orgId, `/api/v1/organisations/${orgId}/capacity-settings`),
};

// Hook to fetch capacity settings for an organisation
export function useCapacitySettings(orgId: string) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: capacitySettingsKeys.settings(orgId),
    queryFn: async () => {
      return OrganisationSettingsService.getApiV1OrganisationsCapacitySettings(orgId);
    },
    enabled: !!orgId && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (
        error instanceof Error &&
        (error.message.includes("401") ||
          error.message.includes("403") ||
          error.message.includes("Unauthorized"))
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// Convenience hook that reads thresholds for the current org without needing to pass orgId
export function useCurrentCapacityThresholds() {
  const { orgId } = useAuthContext();
  const { data: settings } = useCapacitySettings(orgId || '');
  return {
    warningThreshold: settings?.thresholds?.warning_percentage ?? DEFAULT_WARNING_THRESHOLD,
    criticalThreshold: settings?.thresholds?.critical_percentage ?? DEFAULT_CRITICAL_THRESHOLD,
  };
}

// Hook to update capacity settings
export function useUpdateCapacitySettings() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: async ({
      orgId,
      settings,
    }: {
      orgId: string;
      settings: CapacitySettings;
    }) => {
      return OrganisationSettingsService.putApiV1OrganisationsCapacitySettings(
        orgId,
        toCapacitySettingsRequest(settings),
      );
    },
    onSuccess: (_data, variables) => {
      // Invalidate capacity settings query
      queryClient.invalidateQueries({
        queryKey: capacitySettingsKeys.settings(variables.orgId),
      });

      // Show success toast
      toast.success(
        i18n._(t({ id: "settings.capacity.success.updated", message: "Capacity settings updated successfully" }))
      );
    },
    onError: (error: Error) => {
      // Show error toast
      toast.error(
        i18n._(
          t({
            id: "settings.capacity.error.updateFailed",
            message: "Failed to update capacity settings",
          })
        ) +
          ": " +
          error.message
      );
    },
  });
}
